/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { DocumentCopyRecordWithDetails } from '../models/DocumentCopyRecordWithDetails';
import type { DocumentCreatePayload } from '../models/DocumentCreatePayload';
import type { DocumentDetail } from '../models/DocumentDetail';
import type { DocumentGroupCountsForCluster } from '../models/DocumentGroupCountsForCluster';
import type { DocumentGroupCountsPayload } from '../models/DocumentGroupCountsPayload';
import type { DocumentPage } from '../models/DocumentPage';
import type { DocumentRead } from '../models/DocumentRead';
import type { DocumentTagCountsForCluster } from '../models/DocumentTagCountsForCluster';
import type { DocumentUpdatePayload } from '../models/DocumentUpdatePayload';
import type { DocumentWithFileDetail } from '../models/DocumentWithFileDetail';
import type { FileWithUrl_Output } from '../models/FileWithUrl_Output';
import type { GenericDocumentDownloadPayload } from '../models/GenericDocumentDownloadPayload';
import type { GetChunksByIdsPayload } from '../models/GetChunksByIdsPayload';
import type { ListDocumentsPayload } from '../models/ListDocumentsPayload';
import type { PaginatedResult } from '../models/PaginatedResult';
import type { VectorChunkRead } from '../models/VectorChunkRead';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class DocumentsService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * List Untagged Documents For All Clusters
     * @returns DocumentDetail Successful Response
     * @throws ApiError
     */
    public listUntaggedDocumentsForAllClusters(): CancelablePromise<Array<DocumentDetail>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/documents/list-untagged-documents-for-all-clusters',
        });
    }

    /**
     * List Documents For Namespace Id
     * @param namespaceId
     * @param requestBody
     * @returns DocumentDetail Successful Response
     * @throws ApiError
     */
    public listDocumentsForNamespaceId(
        namespaceId: string,
        requestBody?: (ListDocumentsPayload | null),
    ): CancelablePromise<Array<DocumentDetail>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/documents/list-documents-for-namespace-id/{namespace_id}',
            path: {
                'namespace_id': namespaceId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Download Url For Document
     * @param documentId
     * @returns FileWithUrl_Output Successful Response
     * @throws ApiError
     */
    public getDownloadUrlForDocument(
        documentId: string,
    ): CancelablePromise<FileWithUrl_Output> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/documents/get-download-url/{document_id}',
            path: {
                'document_id': documentId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Chunks For Document
     * Get all vector chunks for a document with their bounding boxes.
     * @param documentId
     * @param indexName
     * @returns VectorChunkRead Successful Response
     * @throws ApiError
     */
    public listChunksForDocument(
        documentId: string,
        indexName?: string,
    ): CancelablePromise<Array<VectorChunkRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/documents/get-chunks/{document_id}',
            path: {
                'document_id': documentId,
            },
            query: {
                'index_name': indexName,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Chunks By Ids
     * Get specific vector chunks by their IDs.
     * @param requestBody
     * @param indexName
     * @returns VectorChunkRead Successful Response
     * @throws ApiError
     */
    public getChunksByIds(
        requestBody: GetChunksByIdsPayload,
        indexName?: string,
    ): CancelablePromise<Array<VectorChunkRead>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/documents/get-chunks-by-ids',
            query: {
                'index_name': indexName,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Upload Document
     * @param entityId
     * @param requestBody
     * @param disableWorkflowTrigger
     * @returns DocumentRead Successful Response
     * @throws ApiError
     */
    public uploadDocument(
        entityId: string,
        requestBody: DocumentCreatePayload,
        disableWorkflowTrigger: boolean = false,
    ): CancelablePromise<DocumentRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/documents/upload-document/{entity_id}',
            path: {
                'entity_id': entityId,
            },
            query: {
                'disable_workflow_trigger': disableWorkflowTrigger,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Upload Documents
     * @param entityId
     * @param requestBody
     * @param disableWorkflowTrigger
     * @returns DocumentRead Successful Response
     * @throws ApiError
     */
    public uploadDocuments(
        entityId: string,
        requestBody: Array<DocumentCreatePayload>,
        disableWorkflowTrigger: boolean = false,
    ): CancelablePromise<Array<DocumentRead>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/documents/upload-documents/{entity_id}',
            path: {
                'entity_id': entityId,
            },
            query: {
                'disable_workflow_trigger': disableWorkflowTrigger,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Document
     * @param documentId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteDocument(
        documentId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/documents/delete-document/{document_id}',
            path: {
                'document_id': documentId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Documents
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteDocuments(
        requestBody: GenericDocumentDownloadPayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/documents/delete-documents/',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Document
     * @param documentId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public updateDocument(
        documentId: string,
        requestBody: DocumentUpdatePayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/documents/update-document/{document_id}',
            path: {
                'document_id': documentId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Documents
     * @param requestBody
     * @returns DocumentWithFileDetail Successful Response
     * @throws ApiError
     */
    public getDocuments(
        requestBody: GenericDocumentDownloadPayload,
    ): CancelablePromise<Array<DocumentWithFileDetail>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/documents/get-documents',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Audit Logs For Documents For Entity
     * @param entityId
     * @param cursor
     * @param pageSize
     * @returns PaginatedResult Successful Response
     * @throws ApiError
     */
    public listAuditLogsForDocumentsForEntity(
        entityId: string,
        cursor?: (string | null),
        pageSize: number = 20,
    ): CancelablePromise<PaginatedResult> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/documents/list-audit-logs-for-documents-for-entity/{entity_id}',
            path: {
                'entity_id': entityId,
            },
            query: {
                'cursor': cursor,
                'page_size': pageSize,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Audit Logs For Document
     * @param documentId
     * @param cursor
     * @param pageSize
     * @returns PaginatedResult Successful Response
     * @throws ApiError
     */
    public listAuditLogsForDocument(
        documentId: string,
        cursor?: (string | null),
        pageSize: number = 20,
    ): CancelablePromise<PaginatedResult> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/documents/list-audit-logs-for-document/{document_id}',
            path: {
                'document_id': documentId,
            },
            query: {
                'cursor': cursor,
                'page_size': pageSize,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Documents For Entity
     * @param entityId
     * @param requestBody
     * @returns DocumentDetail Successful Response
     * @throws ApiError
     */
    public listDocumentsForEntity(
        entityId: string,
        requestBody?: (ListDocumentsPayload | null),
    ): CancelablePromise<Array<DocumentDetail>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/documents/list-documents-for-entity',
            path: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Processing Documents For Entity
     * List all documents with index_status of PROCESSING for an entity.
     *
     * Args:
     * entity_id: The ID of the entity
     *
     * Returns:
     * List of DocumentRead objects with index_status PROCESSING
     * @param entityId
     * @returns DocumentRead Successful Response
     * @throws ApiError
     */
    public listProcessingDocumentsForEntity(
        entityId: string,
    ): CancelablePromise<Array<DocumentRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/documents/list-processing-documents',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Document Copy Records For Entity
     * @param entityId
     * @returns DocumentCopyRecordWithDetails Successful Response
     * @throws ApiError
     */
    public listDocumentCopyRecordsForEntity(
        entityId: string,
    ): CancelablePromise<Array<DocumentCopyRecordWithDetails>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/documents/list-document-copy-records-for-entity/',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Documents For Cluster Paginated
     * @param clusterId
     * @param page
     * @param pageSize
     * @param sortBy
     * @param sortOrder
     * @param requestBody
     * @returns DocumentPage Successful Response
     * @throws ApiError
     */
    public listDocumentsForClusterPaginated(
        clusterId: string,
        page: number = 1,
        pageSize: number = 50,
        sortBy?: (string | null),
        sortOrder: 'asc' | 'desc' = 'desc',
        requestBody?: (ListDocumentsPayload | null),
    ): CancelablePromise<DocumentPage> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/documents/list-documents-for-cluster-paginated',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'page': page,
                'page_size': pageSize,
                'sort_by': sortBy,
                'sort_order': sortOrder,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Document Tag Counts For Cluster
     * @param clusterId
     * @param requestBody
     * @returns DocumentTagCountsForCluster Successful Response
     * @throws ApiError
     */
    public listDocumentTagCountsForCluster(
        clusterId: string,
        requestBody?: (ListDocumentsPayload | null),
    ): CancelablePromise<DocumentTagCountsForCluster> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/documents/list-document-tag-counts-for-cluster',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Document Group Counts For Cluster
     * @param clusterId
     * @param requestBody
     * @returns DocumentGroupCountsForCluster Successful Response
     * @throws ApiError
     */
    public listDocumentGroupCountsForCluster(
        clusterId: string,
        requestBody: DocumentGroupCountsPayload,
    ): CancelablePromise<DocumentGroupCountsForCluster> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/documents/list-document-group-counts-for-cluster',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Copy Documents To Entity
     * @param clusterId
     * @param sourceEntityId
     * @param targetEntityId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public copyDocumentsToEntity(
        clusterId: string,
        sourceEntityId: string,
        targetEntityId: string,
        requestBody: Array<string>,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/documents/copy-documents-to-entity',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'source_entity_id': sourceEntityId,
                'target_entity_id': targetEntityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Audit Logs For Documents For Cluster
     * @param clusterId
     * @param cursor
     * @param pageSize
     * @returns PaginatedResult Successful Response
     * @throws ApiError
     */
    public listAuditLogsForDocumentsForCluster(
        clusterId: string,
        cursor?: (string | null),
        pageSize: number = 20,
    ): CancelablePromise<PaginatedResult> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/documents/list-audit-logs-for-documents-for-cluster',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'cursor': cursor,
                'page_size': pageSize,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

}
